import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  faCheck,
  faPaperPlane,
  faInfoCircle,
  faTimes,
  faTrash,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { firstValueFrom, take } from 'rxjs';
import { Feature } from 'src/app/access-management/models/feature';
import { IRole } from 'src/app/access-management/models/role';
import { SubFeature } from 'src/app/access-management/models/sub-feature';
import { RoleService } from 'src/app/access-management/services/role.service';
import { CsaAuthService } from 'src/app/auth/csa-auth.service';
import { User } from 'src/app/models/user';
import { DialogComponent, DialogData, DialogMode } from 'src/app/shared/dialog/dialog.component';
import { SurveysService } from 'src/app/surveys/surveys.service';

@Component({
  selector: 'app-select-session-role',
  templateUrl: './select-session-role.component.html',
  styleUrls: ['./select-session-role.component.scss']
})
export class SelectSessionRoleComponent implements OnInit {
  faCheck = faCheck;

  faInfoCircle = faInfoCircle;

  faPaperPlane = faPaperPlane;

  faPlus = faPlus;

  faTimes = faTimes;

  faTrash = faTrash;

  availableDivisions = [];

  availableRoles: IRole[] = [];

  user: User = {};

  userOverride: object = { division: '' };

  currentlySelectedRole: IRole;

  isLocationSelectionValid = false;

  selectedLocation;

  isLoading = false;

  constructor(private surveyService: SurveysService, private dialog: MatDialog, private router: Router, private csaAuth: CsaAuthService, private roleService: RoleService) { }

  ngOnInit() {
    this.user = this.csaAuth.user;
    this.userOverride['division'] = this.user.division;
    // this.userOverride['division'] = '';
    this.surveyService
      .getNextLevel(`?domain=${this.user['domain']}`, true)
      .subscribe((divisionList) => {
        this.availableDivisions = divisionList['division'];
        this.getRolesAndOrgStructure();
      });
  }

  onRoleChange(role: IRole) {
    this.currentlySelectedRole = role;
    this.userOverride['role'] = this.currentlySelectedRole.name;
  }

  async getRolesAndOrgStructure() {
    // reset the values
    delete this.userOverride['orgStruct'];
    this.availableRoles = [];
    this.currentlySelectedRole = null;

    const [orgStructure, rolesResponse] = await Promise.all([
      firstValueFrom(this.surveyService.getOrgStructure(this.userOverride['division'])),
      firstValueFrom(this.roleService.getListForDivision(this.userOverride['division']))
    ]);

    this.userOverride['orgStruct'] = orgStructure;
    this.availableRoles = rolesResponse.roleList;
  }

  isSelectionValid(): boolean {
    const isLocationValid = this.currentlySelectedRole?.levelName === 'Global' || this.isLocationSelectionValid;

    return isLocationValid && this.userOverride['division'] && this.userOverride['orgStruct'] && !!this.currentlySelectedRole;
  }

  getFeatureSubfeatureKeysFromRole(role: IRole): string[] {
    return role.features?.flatMap((feature: Feature) => feature.children.filter((subfeature: SubFeature) => subfeature.isEnabled)
      .map((subfeature) => `${feature.key}.${subfeature.key}`));
  }

  saveForm() {
    this.isLoading = true;

    // go through the location object and update each field on the user overrides
    if (this.selectedLocation) {
      for (const locationKey in this.selectedLocation) {
        const locationValue = this.selectedLocation[locationKey];
        this.userOverride[locationKey] = locationValue;
      }
    }
    
    this.userOverride['support'] = true;

    // Override user featureSubfeatureKeys for a matching division role.
    this.roleService.getListForDivisionAndRole(this.userOverride['division'], this.userOverride['role']).subscribe(
      (divisionRoles) => {
        const matchingRole = divisionRoles['roleList'].find((listItem) => listItem.name === this.userOverride['role']);
        if (!matchingRole) {
          this.dialog.open(DialogComponent, {
            data: <DialogData>{
              title: 'Matching role error',
              message: `No matching role: ${this.userOverride['role']} found for division: ${this.userOverride['division']}`,
              mode: DialogMode.Error,
            },
            disableClose: true,
            backdropClass: 'dialog-backdrop',
          });
          return;
        }
        this.roleService.fetchForDivision(matchingRole?.id, this.userOverride['division']).pipe(
          take(1),
        ).subscribe((divisionRole: IRole) => {
          const featureSubfeatureKeys = this.getFeatureSubfeatureKeysFromRole(divisionRole);

          this.userOverride['featureSubfeatureKeys'] = featureSubfeatureKeys;

          // loop through the overrides for the selected access and apply them to the user 
          for (const userOverrideKey in this.userOverride) {
            const userOverrideValue = this.userOverride[userOverrideKey];
            this.csaAuth.setUserOverride(userOverrideKey as any, userOverrideValue);
            if (userOverrideKey === 'role' && matchingRole['id']) { // ensure division specific role id is impersonated as well
              this.csaAuth.setUserOverride('roleId', matchingRole['id']);
            }
          }

          this.router.navigate(['/']);
        });
      }
    );
  }
}
